import { Capacitor } from '@capacitor/core';
import {
  Purchases,
  LOG_LEVEL,
  type PurchasesPackage,
  type CustomerInfo,
} from '@revenuecat/purchases-capacitor';
import logger from '@/utils/logger';
import { extractRevenueCatErrorInfo } from './revenuecatError';

// Entitlement configurato su RevenueCat (dashboard → Entitlements).
const PREMIUM_ENTITLEMENT_ID = 'premium';

/**
 * Esito dell'acquisto nativo. Permette alla UI di distinguere l'annullamento
 * (nessun errore da mostrare) dai fallimenti reali, e i fallimenti li traccia
 * su Supabase/Sentry (logger.logError) per diagnosi da remoto senza logcat.
 */
export type PurchaseOutcome =
  | 'success'
  | 'cancelled'
  | 'not_configured'
  | 'no_offerings'
  | 'error';

const isNative = () => Capacitor.isNativePlatform();
let configured = false;

/**
 * Inizializza RevenueCat (solo su piattaforma nativa). Idempotente.
 * La public SDK key va in VITE_REVENUECAT_API_KEY (è una chiave pubblica lato
 * client → ok come VITE_). Senza key il billing nativo resta disattivato.
 */
export async function initRevenueCat(appUserId?: string | null): Promise<void> {
  if (!isNative() || configured) return;
  const apiKey = import.meta.env.VITE_REVENUECAT_API_KEY as string | undefined;
  if (!apiKey) {
    console.warn('[RevenueCat] VITE_REVENUECAT_API_KEY mancante: billing nativo disabilitato');
    return;
  }
  try {
    if (import.meta.env.DEV) {
      await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });
    }
    await Purchases.configure({ apiKey, appUserID: appUserId ?? undefined });
    configured = true;
  } catch (e) {
    logger.logError('billing/revenuecat', 'configure error', {
      errorCode: 'rc_configure_error',
      additionalInfo: extractRevenueCatErrorInfo(e),
    });
  }
}

/** Lega/slega l'utente RevenueCat all'id Supabase (al login/logout). */
export async function setBillingUser(userId: string | null): Promise<void> {
  if (!isNative() || !configured) return;
  try {
    if (userId) {
      await Purchases.logIn({ appUserID: userId });
    } else {
      await Purchases.logOut();
    }
  } catch (e) {
    console.error('[RevenueCat] setBillingUser error', e);
  }
}

function hasPremium(info: CustomerInfo | undefined): boolean {
  return !!info?.entitlements?.active?.[PREMIUM_ENTITLEMENT_ID];
}

/** Stato premium lato client (verità server = profiles.is_premium via webhook). */
export async function isPremiumActiveNative(): Promise<boolean> {
  if (!isNative() || !configured) return false;
  try {
    const { customerInfo } = await Purchases.getCustomerInfo();
    return hasPremium(customerInfo);
  } catch (e) {
    console.error('[RevenueCat] getCustomerInfo error', e);
    return false;
  }
}

async function getPremiumPackage(): Promise<PurchasesPackage | null> {
  try {
    const offerings = await Purchases.getOfferings();
    const current = offerings.current;
    if (!current) {
      // Offering "current"/default assente: Play Billing non espone prodotti
      // (propagazione in corso, tester/region non valido, Offering non default…).
      logger.logError('billing/revenuecat', 'getOfferings: nessun Offering "current"', {
        errorCode: 'rc_no_current_offering',
        additionalInfo: { offeringsAll: Object.keys(offerings.all ?? {}) },
      });
      return null;
    }
    return current.monthly ?? current.availablePackages[0] ?? null;
  } catch (e) {
    logger.logError('billing/revenuecat', 'getOfferings error', {
      errorCode: 'rc_get_offerings_error',
      additionalInfo: extractRevenueCatErrorInfo(e),
    });
    return null;
  }
}

/** Avvia l'acquisto premium nativo. Vedi PurchaseOutcome per gli esiti. */
export async function purchasePremiumNative(): Promise<PurchaseOutcome> {
  if (!isNative() || !configured) {
    logger.logError('billing/revenuecat', 'purchase: SDK RevenueCat non configurato', {
      errorCode: 'rc_not_configured',
      additionalInfo: { isNative: isNative(), configured },
    });
    return 'not_configured';
  }
  const pkg = await getPremiumPackage();
  if (!pkg) {
    // getPremiumPackage ha già tracciato il dettaglio su Supabase/Sentry.
    return 'no_offerings';
  }
  try {
    const { customerInfo } = await Purchases.purchasePackage({ aPackage: pkg });
    return hasPremium(customerInfo) ? 'success' : 'error';
  } catch (e: unknown) {
    // L'annullamento utente non è un errore da loggare come tale.
    if ((e as { userCancelled?: boolean })?.userCancelled) return 'cancelled';
    logger.logError('billing/revenuecat', 'purchase error', {
      errorCode: 'rc_purchase_error',
      additionalInfo: extractRevenueCatErrorInfo(e),
    });
    return 'error';
  }
}

/** Ripristina acquisti (cambio dispositivo / reinstallazione). */
export async function restorePremiumNative(): Promise<boolean> {
  if (!isNative() || !configured) return false;
  try {
    const { customerInfo } = await Purchases.restorePurchases();
    return hasPremium(customerInfo);
  } catch (e) {
    console.error('[RevenueCat] restore error', e);
    return false;
  }
}
